import { FC, useRef } from "react";

import { Box, Stack } from "@mui/material";
import { hasAddOnRates, Ratio, TicketingFormCategory } from "@simplyk/common";
import { useForm, useWatch } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { MAX_IMAGE_SIZES } from "../../../../../common/constants/image";
import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { trpc } from "../../../../../common/helpers/trpc";
import { useMediaQuery } from "../../../../../common/hooks/useMediaQuery";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "../FormStepContainer";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { Display } from "@/components/Display/Display";
import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { ImageUploadContext, ImageUploaderHandle } from "@/components/ImageUploader/types";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { getStepETicketTranslations, getDetailsLink } from "@/features/LiveFormEditor/Steps/StepETicket/ETicket.helper";
import { ETicketPreviewInFocus } from "@/features/LiveFormEditor/types/LiveFormEditor.types";
import { Delete as DeleteIcon } from "@/icons/outlined";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

export type StepETicketFormData = {
  generateQrCode: boolean;
  generateAddOnQrCode: boolean;
  bannerPdf: string | null;
  isBannerPdfUploading: boolean;
  eTicketPreviewInFocus: ETicketPreviewInFocus;
};

export const TicketingLiveEditorStepETicket: FC = () => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();
  const { apiFormObject: apiTicketing, stepProgress, currentStepIndex } = useLiveFormEditorContext<"ticketing">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const { liveFormAmplitudePayload } = useLiveFormAmplitudeEvents();
  const bannerPdfRef = useRef<ImageUploaderHandle>(null);

  const isBannerSupported = apiTicketing.formCategory !== TicketingFormCategory.MembershipV2;
  const isSquareBanner = apiTicketing.isSquareBanner ?? true;
  const hasAlreadyPassedStep = stepProgress.reached > currentStepIndex;
  const isSweepstakes = isSweepstakesCategory(apiTicketing.formCategory);
  const defaultBannerPdf = !isBannerSupported
    ? null
    : hasAlreadyPassedStep
      ? apiTicketing.bannerPdfUrl
      : apiTicketing.bannerUrl;

  const defaultValues: StepETicketFormData = {
    bannerPdf: defaultBannerPdf || null,
    isBannerPdfUploading: false,
    generateQrCode: apiTicketing.generateQrCode,
    generateAddOnQrCode: apiTicketing.generateAddOnQrCode,
    eTicketPreviewInFocus: "e-ticket",
  };
  const form = useForm<StepETicketFormData>({ defaultValues });
  const { control } = form;

  const watchedBannerPdf = useWatch({ control, name: "bannerPdf" });
  const watchedGenerateQrCode = useWatch({ control, name: "generateQrCode" });

  const updateETicket = trpc.updateETicket.useMutation();
  const { handleSaveAndBack, handleSaveAndNext } = useFormSaveAndNavigate<StepETicketFormData>({
    type: "ticketing",
    form,
    alwaysSaveOnNext: Boolean(!apiTicketing.bannerPdfUrl && watchedBannerPdf), // Save the banner pdf if user keeps the default banner pdf (form not dirty)
    saveData: async (formData) => {
      // Finalize any pending square crop before saving
      const finalBannerPdfUrl = (await bannerPdfRef.current?.finalizeSquareCrop()) ?? formData.bannerPdf;

      return await updateETicket.mutateAsync({
        ticketingId: apiTicketing.id,
        bannerPdfUrl: finalBannerPdfUrl,
        generateQrCode: formData.generateQrCode,
        generateAddOnQrCode: formData.generateQrCode && formData.generateAddOnQrCode,
      });
    },
  });

  const stepETicketTranslations = getStepETicketTranslations(apiTicketing.formCategory, t, isSweepstakes);
  const link = getDetailsLink(locale, apiTicketing.formCategory);

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const isAddOnCouponEnabled = formData.generateQrCode && formData.generateAddOnQrCode;
      liveDataToMutate.ticketing.generateQrCode = formData.generateQrCode;
      liveDataToMutate.ticketing.generateAddOnQrCode = isAddOnCouponEnabled;
      liveDataToMutate.ticketing.bannerPdfUrl = formData.bannerPdf;
      liveDataToMutate.eTicketPreviewInFocus = isAddOnCouponEnabled ? formData.eTicketPreviewInFocus : "e-ticket";
    },
  });

  const isBannerPdfUploading = useWatch({ control, name: "isBannerPdfUploading" });

  const focusETicketPreview = () => {
    form.setValue("eTicketPreviewInFocus", "e-ticket");
  };

  const deleteImage = () => {
    focusETicketPreview();
    form.setValue("bannerPdf", null, { shouldValidate: true, shouldDirty: true });
    if (bannerPdfRef.current) {
      bannerPdfRef.current.setImage(null);
    }
  };

  return (
    <FormStepContainer
      title={stepETicketTranslations.title}
      description={
        <>
          {stepETicketTranslations.description}
          {!isSweepstakes && (
            <DashboardLink underline="hover" href={link} target="_blank" sx={{ marginLeft: 1 }}>
              {t("dashboard", "ticketing.knowMore")}
            </DashboardLink>
          )}
        </>
      }
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        disableNext: isBannerPdfUploading,
        disableBack: isBannerPdfUploading,
      }}
      form={form}
    >
      <LiveEditorBlock>
        <LiveEditorToggle
          label={stepETicketTranslations.switchLabel}
          name="generateQrCode"
          control={form.control}
          data-test="ticketing-rates-qr-code-generation-switch"
          onValueChange={(checked) => {
            logUserActionEvent(checked ? AmplitudeEvents.FormEticketAdded : AmplitudeEvents.FormEticketRemoved);
            focusETicketPreview();
          }}
        />
      </LiveEditorBlock>

      <Display when={watchedGenerateQrCode && hasAddOnRates(apiTicketing.rates)}>
        <LiveEditorBlock marginTop={2}>
          <LiveEditorToggle
            label={t("dashboard", "ticketing.setETicket.addOnSwitchLabel")}
            name="generateAddOnQrCode"
            control={form.control}
            data-test="ticketing-add-on-qr-code-generation-switch"
            onValueChange={(checked) => {
              form.setValue("eTicketPreviewInFocus", checked ? "coupon" : "e-ticket");
            }}
          />
        </LiveEditorBlock>
      </Display>

      <Display when={watchedGenerateQrCode && isBannerSupported}>
        <LiveEditorBlock gap={1} marginTop={2}>
          <Stack direction="row" justifyContent="space-between">
            <DashboardTypography variant="subtitle1">
              {t("dashboard", isSweepstakes ? "ticketing.setETicket.sweepstakes.banner" : "ticketBanner")}
            </DashboardTypography>
            <DashboardIconButton
              size="small"
              onClick={deleteImage}
              vibe="neutral"
              data-test="delete-e-ticket-banner-button"
            >
              <DeleteIcon />
            </DashboardIconButton>
          </Stack>

          <Box
            sx={{
              width: "100%",
              ...(isSquareBanner && {
                maxWidth: 400,
                margin: "0 auto",
              }),
            }}
          >
            {/* Image Uploader */}
            {isSquareBanner ? (
              <ImageUploader
                ref={bannerPdfRef}
                data-test="e-ticket-banner-image-input"
                field={{ form, name: "bannerPdf", isUploadingName: "isBannerPdfUploading" }}
                editor={{ kind: "square", targetSize: 800, previewSize: 800 }}
                layout={{ ratio: Ratio.Square }}
                upload={{
                  context: ImageUploadContext.LiveFormEditorETicket,
                  amplitudePayload: liveFormAmplitudePayload,
                }}
                library={
                  isSmallScreen ? undefined : { allowZeffyImages: true, allowUnsplash: true, defaultTab: "zeffyImages" }
                }
                labels={{ import: t("dashboard", "donationForm.uploadETicketBannerV2") }}
              />
            ) : (
              <ImageUploader
                data-test="e-ticket-banner-image-input"
                field={{ form, name: "bannerPdf", isUploadingName: "isBannerPdfUploading" }}
                editor={{ kind: "crop", maxDimensions: MAX_IMAGE_SIZES["banner"] }}
                layout={{ ratio: Ratio.Landscape }}
                upload={{
                  context: ImageUploadContext.LiveFormEditorETicket,
                  amplitudePayload: liveFormAmplitudePayload,
                }}
                library={
                  isSmallScreen ? undefined : { allowZeffyImages: true, allowUnsplash: true, defaultTab: "zeffyImages" }
                }
                labels={{ import: t("dashboard", "donationForm.uploadETicketBannerV2") }}
              />
            )}
          </Box>
        </LiveEditorBlock>
      </Display>
    </FormStepContainer>
  );
};
